import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Rocket, AlertTriangle, CheckCircle2, Loader2, RefreshCw, Banknote, Send,
} from "lucide-react";

function StatCard({ label, value, tone = "slate", testid }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone]}`} data-testid={testid}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs mt-1">{label}</div>
    </div>
  );
}

function statusBadge(status) {
  const tone = status === "ready" ? "bg-emerald-100 text-emerald-800"
    : status === "warning" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";
  return <Badge className={`${tone} hover:${tone} capitalize`}>{status}</Badge>;
}

export default function LaunchReadiness() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/platform/launch-readiness");
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-6" data-testid="launch-readiness-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading launch readiness…
      </div>
    );
  }
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div data-testid="launch-readiness" className="space-y-4">
      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Rocket className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-slate-900">Launch Readiness</h2>
                <p className="text-sm text-slate-500">
                  Migration audit across {s.totalActiveCampaigns} active campaigns. No automatic migration.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={data.summary?.platformFallbackEnabled ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"} data-testid="lr-flag-badge">
                Platform fallback: {data.summary?.platformFallbackEnabled ? "ENABLED" : "DISABLED"}
              </Badge>
              <Button variant="outline" size="sm" onClick={load} data-testid="lr-refresh-btn" className="gap-1">
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Campaigns Ready" value={s.campaignsReady} tone="emerald" testid="lr-ready" />
            <StatCard label="Missing Payment Destination" value={s.missingPaymentDestination} tone="rose" testid="lr-missing-payment" />
            <StatCard label="Missing Delivery Destination" value={s.missingDeliveryDestination} tone="rose" testid="lr-missing-delivery" />
            <StatCard label="Missing Both" value={s.missingBoth} tone="rose" testid="lr-missing-both" />
            <StatCard label="Using Legacy Fallback" value={s.usingLegacyFallback} tone="amber" testid="lr-legacy" />
            <StatCard label="Warning" value={s.campaignsWarning} tone="amber" testid="lr-warning" />
            <StatCard label="Blocked" value={s.campaignsBlocked} tone="rose" testid="lr-blocked" />
            <StatCard label="Active Campaigns" value={s.totalActiveCampaigns} tone="slate" testid="lr-total" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-none">
        <CardContent className="p-6">
          <h3 className="font-semibold text-slate-900 mb-3">Migration Audit</h3>
          <div className="overflow-x-auto" data-testid="lr-audit-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Campaign</th>
                  <th className="py-2 pr-3">Beneficiary</th>
                  <th className="py-2 pr-3">Payment Route</th>
                  <th className="py-2 pr-3">Delivery</th>
                  <th className="py-2 pr-3">Readiness</th>
                  <th className="py-2 pr-3">Blockers</th>
                </tr>
              </thead>
              <tbody>
                {(data.campaigns || []).map((c) => (
                  <tr key={c.campaignId} className="border-b border-slate-100 align-top" data-testid={`lr-row-${c.campaignId}`}>
                    <td className="py-2.5 pr-3 font-medium text-slate-900">{c.campaignName || "—"}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{c.beneficiaryOrganisationName || <span className="text-rose-500">unassigned</span>}</td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      <span className="flex items-center gap-1">
                        <Banknote className="h-3 w-3 text-slate-400" />
                        {c.usingLegacyFallback ? <span className="text-amber-600">legacy fallback</span> : (c.paymentRoute?.source || "—")}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-600">
                      {(c.deliveryRoute || []).length ? (
                        <span className="flex items-center gap-1"><Send className="h-3 w-3 text-slate-400" />{c.deliveryRoute.join(", ")}</span>
                      ) : <span className="text-rose-500">none</span>}
                    </td>
                    <td className="py-2.5 pr-3">{statusBadge(c.readinessStatus)}</td>
                    <td className="py-2.5 pr-3">
                      {(c.blockers || []).length ? (
                        <ul className="space-y-0.5">
                          {c.blockers.map((b, i) => (
                            <li key={i} className="text-xs text-rose-700 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />{b}</li>
                          ))}
                        </ul>
                      ) : <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />none</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
